import { useMutation, UseMutationResult } from '@tanstack/react-query'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import { Cart } from '@/types/cart'

const CART_DISCOUNT_REMOVE_MUTATION = `
  mutation removeCartDiscount($input: CartDiscountRemoveInput!) {
    removeCartDiscount(input: $input) {
      success
      message
      cart {
        id
        displayId
        totalAmountCents
        cartToken
        orderId
        coupons {
          coupon {
            code
          }
          amountCents
        }
        giftCards {
          giftCard {
            displayCode
            id
          }
          amountCents
        }
        orderSummary {
          id
          legs {
            id
            legIndex
            legName
            pickupFeeCents
            travelers {
              id
              name
              email
              phone
              shipments {
                id
                trackingId
                totalPriceCents
                retailShipRate
                paidInsuranceCents
                handlingFeeCents
                fuelSurchargeCents
                clearanceFeeCents
                upgradeDiscount
                shipmentLegName
                wholesaleDiscountCents
                shipDate
                arrivalDate
                packagingId
                additionalInfo
                products {
                  id
                  fullName
                  displayName
                  priceCents
                  type
                  coverageAmountCents
                  productLine {
                    id
                    name
                    icon
                    displayName
                  }
                }
              }
            }
            origin {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
            destination {
              address1
              address2
              city
              state
              zip5
              countryCode
            }
          }
        }
      }
    }
  }
`

export interface CartDiscountRemoveInput {
  cartToken: string
}

export interface CartDiscountRemoveResult {
  cart: Cart
  message: string
  success: boolean
}

export interface CartDiscountRemoveResponse {
  removeCartDiscount: CartDiscountRemoveResult
}

export const useCartDiscountRemoveApi = (): UseMutationResult<
  CartDiscountRemoveResult,
  Error,
  CartDiscountRemoveInput,
  unknown
> => {
  const client = useGraphQLClient() // Avoid re-creating the client on every mutation

  const removeCartDiscountApi = async (
    input: CartDiscountRemoveInput,
  ): Promise<CartDiscountRemoveResult> => {
    const result = await client.request<CartDiscountRemoveResponse>(CART_DISCOUNT_REMOVE_MUTATION, {
      input,
    })

    if (!result?.removeCartDiscount) {
      throw new Error('No cart returned from API')
    }

    return result.removeCartDiscount
  }

  return useMutation<CartDiscountRemoveResult, Error, CartDiscountRemoveInput>({
    mutationFn: removeCartDiscountApi,
  })
}

export default useCartDiscountRemoveApi
